import { faStar, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Node } from '@react-types/shared';
import { useField } from 'formik';
import { uniqBy } from 'lodash';
import {
  AnchorHTMLAttributes,
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useListBox, useOption } from 'react-aria';
import { ComboBoxState } from 'react-stately';
import {
  NetworkPoint,
  NetworkPointsParams,
} from 'src/apis/network-points/types';
import { ActionGroup } from 'src/components/buttons-and-actions/action-group';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Direction } from 'src/components/data-display/direction';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { Table } from 'src/components/data-display/smart-table';
import { Tbody, Td, Th, Tr, TrProps } from 'src/components/data-display/table';
import {
  FormModeContextValue,
  useFormMode,
} from 'src/components/form/form-mode-context';
import { SelectOption } from 'src/components/form/select';
import {
  NetworkPointsRequest,
  useNetworkPoints,
} from 'src/components/form/select/network-point-select/use-network-points';
import {
  NetworkPointsRootRequest,
  useNetworkPointsRoot,
} from 'src/components/form/select/network-point-select/use-network-points-root';
import {
  ComboboxChildrenProps,
  getSearchableSelectSchema,
  SearchableSingleSelect,
} from 'src/components/form/select/searchable-single-select';
import { Stack } from 'src/components/layout/stack';
import { Link } from 'src/components/navigation/link';
import { Spinner, SpinnerContainer } from 'src/components/spinner-container';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { useSearch } from 'src/hooks/use-search';
import { useToast } from 'src/hooks/use-toasts';
import { Colors, flyoutStyles } from 'src/styles';
import { handleRequestErrorWithNotification } from 'src/utils/axios-config';
import { handleFakeOpenInBackgroundTab } from 'src/utils/open-in-background-tab';
import styled from 'styled-components';
import { string, object } from 'yup';

type InitialNetworkPointOption = SelectOption & { networkPoint?: NetworkPoint };
type NetworkPointOption = SelectOption & { networkPoint: NetworkPoint };

export const networkPointSelectSchema = getSearchableSelectSchema(string(), {
  networkPoint: object<NetworkPoint>().defined(),
});

type InternalParams = {
  q: string | null;
  favouritesAboveIfInitialSearch: boolean;
  case: 'SHOW_ALL' | 'ACTIVE_ONLY' | 'ONLY_FAVORITE' | 'ONLY_OWN';
  limit: number;
  offset: number;
};

type NetworkPointSelectProps = {
  name: string;
  label: string;
  hideLabel?: boolean;
  stacked?: boolean;
  inline?: boolean;
  initialSearch?: string;
  disabled?: boolean;
  placeholder?: string;
  initialOpen?: boolean;
  hint?: string;
  onChange?: (value: string | '') => void;
  /**
   * Mandatory if form mode is 'search'. Not usedf in other form modes.
   */
  pending?: boolean;
};

export function NetworkPointSelect(props: NetworkPointSelectProps) {
  const [field, fieldMeta, fieldHelper] = useField<
    InitialNetworkPointOption | undefined
  >(props.name);
  const handleInitialSearch =
    // if someone set an initial field value, but no networkPoint was set, we trigger an initial search
    field.value && !field.value.networkPoint ? field.value.label : '';

  const {
    name,
    label,
    placeholder,
    stacked,
    inline,
    hideLabel = false,
    disabled,
    initialOpen,
    hint,
    initialSearch = handleInitialSearch,
    onChange,
    pending,
  } = props;
  const formMode = useFormMode();

  if (formMode === 'search' && pending === undefined)
    throw new Error('The "pending" property is mandatory in "search" mode.');

  const networkPointsRoot = useNetworkPointsRoot({
    // special error handling case: we'd like to immidiately ask for the network points root,
    // but we'd like to postpone an error notification until someone actually interacts with the search
    onError(error) {
      return error;
    },
  });
  const notify = useToast();
  const [_, setReauthenticate] = useReauthenticate();
  const firstUserInteraction = useRef(true);

  const [internalParams, setInternalParams] = useState<InternalParams>({
    q: initialSearch,
    favouritesAboveIfInitialSearch: false,
    case: 'ACTIVE_ONLY',
    limit: 20,
    offset: 0,
  });

  const params = useMemo(() => {
    const params: NetworkPointsParams = {
      q: internalParams.q,
      offset: internalParams.offset,
      limit: internalParams.limit,
      favouritesAboveIfInitialSearch:
        internalParams.favouritesAboveIfInitialSearch,
      favorite:
        internalParams.case === 'ONLY_FAVORITE' ? 'ONLY_FAVORITE' : 'ALL',
      own: internalParams.case === 'ONLY_OWN' ? 'ONLY_OWN' : 'ALL',
      active: internalParams.case === 'ACTIVE_ONLY' ? 'ACTIVE' : 'ALL',
    };
    return params;
  }, [internalParams]);

  const minLength = 0;

  const [searchValue, setSearchValue, searchQuery] = useSearch(initialSearch, {
    minLength,
    delay: 100,
  });

  const networkPoints = useNetworkPoints();

  useEffect(() => {
    if (!networkPointsRoot.response) return;
    networkPoints.execute({ networkPointsRoot, params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkPointsRoot.response, params]);

  useEffect(() => {
    // this is only relevant if we had a forced initial search,
    // in that case we'll try to find the network point for the initial value
    // (and if we can't find the network point, we'll set the field value to undefined)
    if (!networkPoints.response) return;
    if (!handleInitialSearch) return;
    const networkPoint = networkPoints.response.data._embedded.items.find(
      (item) => item.id === field.value?.value
    );
    if (networkPoint)
      fieldHelper.setValue({
        label: networkPoint.name,
        value: networkPoint.id,
        networkPoint,
      });
    else fieldHelper.setValue(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkPoints.response]);

  useEffect(() => {
    setInternalParams((params) => ({
      ...params,
      q: searchQuery,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const loadMoreNetworkPoints = useNetworkPoints();

  // whenever params change we reset the "load more" request
  useEffect(() => {
    loadMoreNetworkPoints.canceler?.();
    loadMoreNetworkPoints.setResponse(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalParams]);

  return (
    <SearchableSingleSelect
      mode="server-search"
      name={name}
      label={label}
      placeholder={placeholder}
      stacked={stacked}
      inline={inline}
      disabled={disabled}
      setSearchValue={setSearchValue}
      minSearchValueLength={minLength}
      searchResult={networkPoints.response?.data._embedded.items.map(
        (item) =>
          ({
            label: item.name,
            value: item.id,
            networkPoint: item,
          }) satisfies NetworkPointOption
      )}
      searchValue={searchValue}
      lastSearchQuery={networkPoints.response?.config.params.infix ?? ''}
      pending={networkPoints.pending}
      hint={hint}
      hideLabel={hideLabel}
      leftAddon={
        formMode === 'search' ? (
          pending ? (
            <Spinner />
          ) : (
            <FontAwesomeIcon icon={faSearch} />
          )
        ) : undefined
      }
      popoverStyle={{ width: '60rem' }}
      children={(props) =>
        // if we had a forced initial search, but no response yet,
        // we show a pending state as we miss the network point data
        !handleInitialSearch ? (
          <SelectPopover
            {...props}
            internalParams={internalParams}
            setInternalParams={setInternalParams}
            params={params}
            networkPointsRoot={networkPointsRoot}
            networkPoints={networkPoints}
            loadMoreNetworkPoints={loadMoreNetworkPoints}
            formMode={formMode}
          />
        ) : (
          <Flyout>
            <Card>
              Initial search in progress... <Spinner />
            </Card>
          </Flyout>
        )
      }
      sortBy="manual"
      onChange={onChange}
      initialOpen={initialOpen}
      onFocus={() => {
        if (!firstUserInteraction.current) return;
        firstUserInteraction.current = false;
        if (!networkPointsRoot.error) return;
        handleRequestErrorWithNotification({
          notify,
          error: networkPointsRoot.error,
          setReauthenticate,
        });
      }}
    />
  );
}

const Flyout = styled.div`
  ${flyoutStyles};
`;

const TableWrapper = styled.div`
  min-width: 17.9rem;
  max-height: 50rem;
  overflow: auto;
`;

const SelectPopover: FC<
  ComboboxChildrenProps & {
    internalParams: InternalParams;
    setInternalParams: Dispatch<SetStateAction<InternalParams>>;
    params: NetworkPointsParams;
    networkPointsRoot: NetworkPointsRootRequest;
    networkPoints: NetworkPointsRequest;
    loadMoreNetworkPoints: NetworkPointsRequest;
    formMode: FormModeContextValue;
  }
> = ({
  searchState,
  comboBoxState: state,
  listBoxOptions,
  listBoxRef,
  name,
  internalParams,
  setInternalParams,
  params,
  networkPointsRoot,
  networkPoints,
  loadMoreNetworkPoints,
  formMode,
}) => {
  const { listBoxProps } = useListBox(listBoxOptions, state, listBoxRef);

  const options = [...state.collection] as Node<NetworkPointOption>[];

  const monolithUser = useOptionalAuthenticatedMonolithUser();

  // merge first network point request with "load more" results
  useEffect(() => {
    if (!networkPoints.response) return;
    if (!loadMoreNetworkPoints.response) return;

    const items = uniqBy(
      [
        ...networkPoints.response.data._embedded.items,
        ...loadMoreNetworkPoints.response.data._embedded.items,
      ],
      'id'
    );

    networkPoints.setResponse({
      ...networkPoints.response,
      data: {
        ...networkPoints.response.data,
        _embedded: { items },
        limit:
          loadMoreNetworkPoints.response.data.offset +
          loadMoreNetworkPoints.response.data.limit,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreNetworkPoints.response]);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // whenever params change we scroll back to the top
  useEffect(() => {
    if (!tableWrapperRef.current) return;
    tableWrapperRef.current.scrollTop = 0;
  }, [internalParams]);

  const linkProps: AnchorHTMLAttributes<HTMLAnchorElement> = {
    target: formMode === 'search' ? undefined : '_blank',
    onClick: (e) => e.stopPropagation(),
  };

  return (
    <Flyout>
      {(monolithUser?.hasShipper || monolithUser?.hasTso) && (
        <QuickFilter
          hasTsoRole={monolithUser.hasTso}
          hasShipperRole={monolithUser.hasShipper}
          internalParams={internalParams}
          setInternalParams={setInternalParams}
        />
      )}

      <SpinnerContainer
        pending={searchState.case === 'PENDING'}
        data-testid="nwp-search-spinner-container"
      >
        <TableWrapper
          ref={tableWrapperRef}
          data-testid="nwp-search-results-scroll-container"
          onScroll={(event) => {
            // when we scroll to the bottom we try to load more network points
            if (!networkPointsRoot.response) return;
            if (!networkPoints.response) return;
            if (networkPoints.pending) return;
            if (loadMoreNetworkPoints.pending) return;

            const element = event.currentTarget;
            const offset = 100;
            const scrollPos = element.scrollHeight - element.scrollTop - offset;
            const reachedBottom = scrollPos < element.clientHeight;

            const data =
              loadMoreNetworkPoints.response?.data ??
              networkPoints.response.data;
            const moreToLoad = data.offset + data.limit < data.total;

            if (!reachedBottom || !moreToLoad) return;

            loadMoreNetworkPoints.execute({
              networkPointsRoot,
              params: { ...params, offset: data.offset + data.limit },
            });
          }}
        >
          <Table
            data-testid="nwp-search-table"
            top="1px"
            data={options}
            setId={(item) => item.key}
            body={({ tbodyProps }) => (
              <Tbody
                {...tbodyProps}
                {...listBoxProps}
                ref={listBoxRef as RefObject<HTMLTableSectionElement>}
              />
            )}
            row={(props) => (
              <SelectRow {...props} state={state} name={name} mode={formMode} />
            )}
            cols={[
              {
                key: 'networkPoint',
                width: 3,
                head: <Th>Network Point</Th>,
                body: (item) => (
                  <Td>
                    {monolithUser?.hasShipper &&
                      item.value.networkPoint.favourite && (
                        <>
                          <FontAwesomeIcon icon={faStar} />{' '}
                        </>
                      )}

                    {formMode === 'search' ? (
                      item.value.networkPoint.name
                    ) : (
                      <Link
                        mode="default-underlined"
                        to={`/transport/network-points/redirect/${item.value.networkPoint.id}`}
                        {...linkProps}
                      >
                        {item.value.networkPoint.name}
                      </Link>
                    )}

                    <br />
                    <Direction networkPoint={item.value.networkPoint} />
                    {!item.value.networkPoint.active && (
                      <>
                        {' '}
                        <small style={{ color: '#7c7c7c', fontWeight: 'bold' }}>
                          Inactive
                        </small>
                      </>
                    )}
                  </Td>
                ),
              },
              {
                key: 'marketer',
                width: 2,
                head: <Th>TSO</Th>,
                body: (item) => (
                  <Td>
                    {item.value.networkPoint.bundle ? (
                      <>
                        <Link
                          mode="default-underlined"
                          to={`/market-information/players/operators/${item.value.networkPoint.exit.operatorId}`}
                          {...linkProps}
                        >
                          {item.value.networkPoint.exit.operatorShortName}
                        </Link>{' '}
                        /{' '}
                        <Link
                          mode="default-underlined"
                          to={`/market-information/players/operators/${item.value.networkPoint.entry.operatorId}`}
                          {...linkProps}
                        >
                          {item.value.networkPoint.entry.operatorShortName}
                        </Link>
                      </>
                    ) : (
                      <Link
                        mode="default-underlined"
                        to={`/market-information/players/operators/${item.value.networkPoint.operatorId}`}
                        {...linkProps}
                      >
                        {item.value.networkPoint.operatorShortName}
                      </Link>
                    )}
                  </Td>
                ),
              },
              {
                key: 'id',
                width: 3,
                head: <Th>Network Point ID</Th>,
                body: (item) => (
                  <Td aria-label={item.textValue}>
                    <p>{item.value.networkPoint.identifier}</p>

                    {showEic(item.value.networkPoint) ? (
                      <small style={{ color: Colors.brandLight2 }}>
                        EIC: {item.value.networkPoint.eic}
                      </small>
                    ) : (
                      <small>
                        <EmptyValue />
                      </small>
                    )}
                  </Td>
                ),
              },
            ]}
            empty={
              searchState.case === 'EMPTY' && searchState.lastSearchQuery
                ? {
                    label: `Could not find network points for "${searchState.lastSearchQuery}".`,
                  }
                : searchState.case === 'EMPTY' && !searchState.lastSearchQuery
                ? {
                    // e.g. favorite filter selected, but no favorite configured
                    label: 'Could not find network points.',
                  }
                : {
                    // e.g. the previous search _was_ empty, but now have a pending state for a new request
                    label: 'Could not find network points.',
                  }
            }
          />
          {loadMoreNetworkPoints.pending && (
            <Stack
              justifyContent="center"
              style={{ margin: '2rem' }}
              data-testid="nwp-search-loading-more"
            >
              <Spinner />
            </Stack>
          )}
        </TableWrapper>
      </SpinnerContainer>
    </Flyout>
  );
};

function showEic(networkPoint: NetworkPoint) {
  if (!networkPoint.eic) return false;
  if (networkPoint.eic === networkPoint.name) return false;
  if (networkPoint.eic === networkPoint.identifier) return false;
  if (networkPoint.eic === 'not applicable') return false;
  if (networkPoint.eic === 'n/a') return false;
  return true;
}

const SelectRow: FC<{
  item: Node<SelectOption<string | null>>;
  state: ComboBoxState<SelectOption<string | null>>;
  name: string;
  trProps: TrProps;
  mode: FormModeContextValue;
}> = ({ item, state, name, trProps, mode = 'form' }) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const { optionProps, isSelected, isFocused } = useOption(
    { key: item.key },
    state,
    ref
  );

  return (
    <Tr
      {...trProps}
      {...optionProps}
      onPointerUp={(event) => {
        if (event.target instanceof HTMLAnchorElement) return;

        // The `<tr/>` is supposed to act as a link for _some cases_ (opening background tabs),
        // but we cannot use a real `<a/>` as it would create invalid markup.
        // (A `<tr/>` cannot be a child or parent of an `<a/>`.)
        handleFakeOpenInBackgroundTab(
          event,
          `${location.origin}/network-points-redirect/${item.value.value}`
        );
        if (event.isPropagationStopped()) return;

        optionProps.onPointerUp?.(event);
      }}
      onPointerLeave={(event) => {
        state.selectionManager.setFocusedKey(null);
        optionProps.onPointerLeave?.(event);
      }}
      // within search mode we don't visualize the selection
      isSelected={mode === 'search' ? false : isSelected}
      ref={ref}
      isFocused={isFocused}
      data-testid={`${name}-option-${item.value.value}`}
    />
  );
};

const QuickFilter: FC<{
  hasTsoRole: boolean | undefined;
  internalParams: InternalParams;
  setInternalParams: Dispatch<SetStateAction<InternalParams>>;
  hasShipperRole: boolean | undefined;
}> = ({ hasTsoRole, internalParams, setInternalParams, hasShipperRole }) => {
  return (
    <Stack justifyItems="end">
      <div style={{ margin: '1rem' }}>
        <ActionGroup mode="button-secondary" size="small">
          {hasTsoRole && (
            <Button
              isActive={internalParams.case === 'SHOW_ALL'}
              onClick={() =>
                setInternalParams((params) => ({
                  ...params,
                  case: 'SHOW_ALL',
                }))
              }
              data-testid="nwp-search-show-all"
            >
              Show All
            </Button>
          )}

          <Button
            isActive={internalParams.case === 'ACTIVE_ONLY'}
            onClick={() =>
              setInternalParams((params) => ({
                ...params,
                case: 'ACTIVE_ONLY',
              }))
            }
            data-testid="nwp-search-active-only"
          >
            {hasShipperRole && !hasTsoRole ? 'Show All' : 'Active Only'}
          </Button>

          {hasShipperRole && (
            <Button
              isActive={internalParams.case === 'ONLY_FAVORITE'}
              onClick={() =>
                setInternalParams((params) => ({
                  ...params,
                  case: 'ONLY_FAVORITE',
                }))
              }
              data-testid="nwp-search-favorites-only"
            >
              <FontAwesomeIcon icon={faStar} /> Favourites Only
            </Button>
          )}

          {hasTsoRole && (
            <Button
              isActive={internalParams.case === 'ONLY_OWN'}
              onClick={() =>
                setInternalParams((params) => ({
                  ...params,
                  case: 'ONLY_OWN',
                }))
              }
              data-testid="nwp-search-own-only"
            >
              Own Only
            </Button>
          )}
        </ActionGroup>
      </div>
    </Stack>
  );
};
